<template>
  <div class="plp">
    <div class="container">
      <AppBreadcrumbs />

      <div class="plp__container">
        <div class="plp__description">
          <!-- <AppLink
            v-if="previousPage"
            :to="`/${previousPage.url}`"
            class="back plp-banner__back"
          >
            <IconArrow class="icon icon--arrow" />
            {{ previousPage.label }}
          </AppLink> -->

          <h1 class="plp__heading heading heading--1">
            <span>{{ pageTitle }}</span>
          </h1>

          <UiReadMore
            v-if="plpPage?.PLPData?.Description"
            class="plp__description--text"
          >
            <StrapiBlocksText :nodes="plpPage.PLPData?.Description" />
          </UiReadMore>
        </div>

        <CatalogBanners
          v-if="plpBanners?.length"
          class="plp__banners"
          :banners="plpBanners"
        />

        <div class="plp__quick-links">
          <div class="plp__quick-links__heading">
            <span class="heading heading--6">{{ $t('All') }} {{ pageTitle }}</span>
            <span class="plp__product-count">{{ totalCount }} artikelen</span>
          </div>

          <CatalogQuickLinks
            v-if="plpQuicklinks?.length"
            :links="plpQuicklinks"
          />
        </div>

        <PaginatorToolbar
          v-if="sortOptions"
          class="plp__paginator plp__paginator--top"
          :sort="sort"
          :sort-options="sortOptions"
          v-on="paginatorMethods"
        />

        <div class="plp__sidebar">
          <!-- <FilterCategories class="plp__categories" :url-data="urlData" /> -->
          <FilterList
            class="plp__filter-list"
            :sort-options="sortOptions"
          />
        </div>

        <div
          id="product-grid"
          class="plp__products products"
          :class="`products--${view}`"
        >
          <FilterTileStrip
            v-for="(strip, stripIndex) in tileStrips"
            :key="strip!.filterKey"
            class="plp__tile-strip"
            :class="`plp__tile-strip--${stripIndex + 1}`"
            :heading="strip!.heading"
            :tiles="strip!.tiles"
            :filter-key="strip!.filterKey"
          />
          <!--          <pre> {{ products }}</pre> -->
          <template
            v-for="(product, index) in products"
            :key="index"
          >
            <AppUsps
              v-if="plpUsps?.length && page === 1 && index === 12"
              class="plp__usps"
              :usps="plpUsps"
            />
            <ProductTile
              v-if="product.__typename === 'EsProduct'"
              :lazy="false"
              :product="product"
              :show-compare="config.showCompare"
              list="ProductListerPage"
            />
            <StrapiBanner
              v-if="product.__typename === 'Banner'"
              class="tw__banner"
              :class="gridStyle(product.visualproperties)"
              :banner="product"
              :tweakwise-id="product.tweakwiseId"
            />
          </template>
        </div>

        <PaginatorToolbar
          class="plp__paginator plp__paginator--bottom"
          :current-page="page"
          :total-pages="totalPages"
          v-on="paginatorMethods"
        />
      </div>
    </div>

    <StrapiSlices
      v-if="plpPage?.Slices?.length"
      :slices="plpPage.Slices"
    />

    <div class="container plp__squeezely-sliders">
      <ThirdpartySqueezelyProductSlider id="spotler-activate-plp-slider-1" />
      <ThirdpartySqueezelyProductSlider id="spotler-activate-plp-slider-2" />
    </div>

    <UiScrollToTop class="plp__scroll-to-top" />

    <ProductCompareCta />

    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
    <!--  -->
  </div>
</template>

<script lang="ts" setup>
const { setBreadcrumbs } = useBreadcrumbs()
const route = useRoute()
const { t } = useI18n({ useScope: 'global' })
const { public: config } = useRuntimeConfig()

const meta = inject('meta', ref())

setBreadcrumbs({
  label: meta?.value?.label || 'Search',
  parents: meta?.value?.parents || [],
})

const { getCompareProductListData, setRootCategory } = useCompareProductList()

onMounted(() => {
  const {
    url: parentCategoryUrl,
    label: parentCategoryLabel,
    parents = [],
  } = meta.value || {}

  setRootCategory({
    url: parents.length ? parents[0].url : parentCategoryUrl,
    name: parents.length ? parents[0].label : parentCategoryLabel,
  })

  getCompareProductListData()
})

const {
  // Request data
  // error,
  // data,
  // loading,

  // Product data
  products,
  fetchProductData,
  sortOptions,
  strapiData,
  getStrapiData,
} = useCatalog(meta)

const gridStyle = (visualproperties: object) => {
  if (visualproperties) {
    return {
      [`col${visualproperties.colspan}_row${visualproperties.rowspan}`]: true,
    }
  }
  return {}
}

const plpPage = computed(() => {
  return strapiData.value?.plpPage
})

const plpUsps = computed(() => {
  return strapiData.value?.plpUsps?.[0]?.Usp || []
})

const plpTooltips = computed(() => {
  return strapiData.value?.tooltips
})

provide('tooltips', plpTooltips)

const plpBanners = computed(() => {
  return plpPage.value?.PLPData?.Banners
})

const plpQuicklinks = computed(() => {
  return plpPage.value?.PLPData?.Quicklinks?.Icons
})

const TILE_FILTER_CONFIG: { paths: string[], filterKeys: string[] }[] = [
  {
    paths: ['laptoptassen', 'laptoptaschen'],
    filterKeys: ['tra_brand', 'tra_laptop_compartment_size'],
  },
  {
    paths: ['tassen', 'taschen'],
    filterKeys: ['tra_brand', 'tra_color'],
  },
]

const tileFilterKeys = computed(() => {
  const categoryPaths = [
    meta.value?.url,
    ...(meta.value?.parents || []).map((parent: any) => parent?.url),
  ]
    .filter(Boolean)
    .map((path: string) => path.replace(/^\/|\/$/g, '').toLowerCase())

  const match = TILE_FILTER_CONFIG.find(config =>
    config.paths.some(configPath =>
      categoryPaths.some(categoryPath =>
        categoryPath === configPath || categoryPath.startsWith(`${configPath}/`),
      ),
    ),
  )

  return match?.filterKeys || []
})

const tileHeading = (filterKey: string) => {
  if (filterKey === 'tra_brand') {
    return t('Choose your favorite brand')
  }

  if (filterKey === 'tra_color') {
    return t('Choose your favorite color')
  }

  if (filterKey === 'tra_laptop_compartment_size') {
    return t('Choose your laptop size')
  }

  return ''
}

const tileStrips = computed(() => {
  return tileFilterKeys.value
    .map((filterKey) => {
      const filter = (filters.value || []).find((item: any) => item.key === filterKey)
      const options = filter?.options || []

      if (!filter || options.some((option: any) => option.active)) {
        return null
      }

      const tiles = options.map((option: any) => ({
        key: option.key,
        label: option.label,
        active: !!option.active,
      }))

      return tiles.length ? { filterKey, heading: tileHeading(filterKey), tiles } : null
    })
    .filter(Boolean)
})

const {
  page,
  totalPages,
  sort,
  // itemsPerPage,
  // itemsPerPageOptions,
  view,
  totalCount,
  paginatorMethods,
} = useCatalogPaginator()

const {
  filters,
} = useCatalogFilters(meta)

await Promise.all([
  fetchProductData(),
  meta?.value?.strapi_id ? getStrapiData() : undefined,
])

const pageTitle = computed(() => {
  const label = meta.value?.label

  if (!label) return ''

  return label
    .split(' ')
    .map(word => (word === word.toUpperCase() ? word : useTitle(word)))
    .join(' ')
})

const metaTitle = computed(
  () => plpPage.value?.MetaData?.MetaTitle || t('plp.metaTitle', { placeholder: pageTitle.value }),
)

const metaDescription = computed(
  () => plpPage.value?.MetaData?.MetaDescription || t('plp.metaDescription', { placeholder: pageTitle.value }),
)

// function resetAndApplyAll () {
//   filtersRemoveAll()
//   filtersApplyAll()
// }

// function toggleActive (filterKey: string, optionKey: string) {
//   optionToggle(filterKey, optionKey)
//   filterApply(filterKey)
// }

// function resetMin (filterKey: string) {
//   filterRangeResetMin(filterKey)
//   filterApply(filterKey)
// }

// function resetMax (filterKey: string) {
//   filterRangeResetMax(filterKey)
//   filterApply(filterKey)
// }

const { pageView, viewItemList } = useDataLayer()
pageView('overview', pageTitle.value)

const metaRobots = computed(() => {
  const { p, filter, color, ...restOfQuery } = route.query
  if (Object.keys(restOfQuery).length > 0) {
    return 'noindex, follow'
  }
  if (plpPage.value?.MetaData?.MetaRobots) {
    return plpPage.value.MetaData?.MetaRobots?.replace('_', ', ')
  }
  return 'index, follow'
})

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: metaRobots },
  ],
})

const canonicalUrl = computed(() => {
  // 1. Explicit per-URL override from Strapi (phased rollout)
  if (plpPage.value?.MetaData?.CanonicalUrl) {
    return plpPage.value.MetaData.CanonicalUrl
  }

  // 2. Non-indexed pages keep the existing filter/color handling
  if (!metaRobots.value.startsWith('index')) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { filter, color, ...restOfQuery } = route.query
    const searchParams = new URLSearchParams({
      ...(color ? { filter, color } : {}),
    })
    return route.path + (searchParams.size > 0 ? '?' + searchParams.toString() : '')
  }

  // 3. Indexed pages: keep the `p` pagination param in the canonical for now —
  //    client wants existing paginated URLs to retain their Google rankings this phase
  return route.fullPath
})

const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({
  item_list_name: 'plp',
  item_list_id: route.fullPath,
}))

const category = computed<Partial<MagentoCategory>>(() => ({
  id: meta.value?.id?.split('-')?.[1],
  name: meta.value?.label,
}))

watch(products, () => {
  viewItemList(products.value, dataLayerOverrides.value, category.value)
}, { immediate: true })

const { canonical } = useSeo()
canonical(canonicalUrl)
</script>

<style>
@import url("~layers/app/pages/~plp.css");
</style>
